import React from 'react';

export function FlourHydration() {
  return (
    <div className="space-y-6 text-white/90" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Finding the Right Hydration Rate</h3>
        <p className="mb-4">
          Hydration is the ratio of water to flour in your dough, expressed as a percentage. Finding the right hydration rate is crucial for achieving the desired texture, crumb structure, and overall quality of your bread.
        </p>
        
        <div className="bg-amber-500/10 p-4 rounded-lg mb-6">
          <h4 className="text-amber-400 font-medium mb-2">Hydration Formula</h4>
          <p className="text-white/80">
            Hydration (%) = (Weight of water ÷ Weight of flour) × 100
          </p>
          <p className="text-sm text-white/70 mt-2">
            Example: 700g water ÷ 1000g flour × 100 = 70% hydration
          </p>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Hydration Ranges by Flour Type</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-white/80">
            <thead>
              <tr className="border-b border-white/20">
                <th className="text-left py-3 px-4 text-amber-400">Flour Type</th>
                <th className="text-left py-3 px-4 text-amber-400">Ideal Hydration Range</th>
                <th className="text-left py-3 px-4 text-amber-400">Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Bread Flour</td>
                <td className="py-3 px-4">65-75%</td>
                <td className="py-3 px-4">Strong gluten development, handles higher hydration well</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">All-Purpose Flour</td>
                <td className="py-3 px-4">60-70%</td>
                <td className="py-3 px-4">Less protein, benefits from slightly lower hydration</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Whole Wheat Flour</td>
                <td className="py-3 px-4">75-85%</td>
                <td className="py-3 px-4">Bran absorbs more water, requires higher hydration</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Rye Flour</td>
                <td className="py-3 px-4">80-100%</td>
                <td className="py-3 px-4">Absorbs significantly more water, sticky at high hydration</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Spelt Flour</td>
                <td className="py-3 px-4">65-75%</td>
                <td className="py-3 px-4">Absorbs water quickly but gluten breaks down easily</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Einkorn Flour</td>
                <td className="py-3 px-4">60-70%</td>
                <td className="py-3 px-4">Lower gluten content, can become soupy at high hydration</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium">Flour Blends</td>
                <td className="py-3 px-4">Varies</td>
                <td className="py-3 px-4">Calculate based on proportions of each flour type</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Effects of Hydration on Bread</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Low Hydration (55-65%)</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-2">
              <li>Tighter, more uniform crumb structure</li>
              <li>Easier to handle and shape</li>
              <li>Less oven spring</li>
              <li>Denser texture</li>
              <li>Longer shelf life</li>
              <li>Good for sandwich breads and beginner bakers</li>
            </ul>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Medium Hydration (65-75%)</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-2">
              <li>Balanced open crumb structure</li>
              <li>Good oven spring</li>
              <li>Moderate handling difficulty</li>
              <li>Versatile for most bread styles</li>
              <li>Good crust development</li>
              <li>Ideal for most home bakers</li>
            </ul>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">High Hydration (75-85%)</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-2">
              <li>Open, irregular crumb structure</li>
              <li>Excellent oven spring</li>
              <li>Challenging to handle (sticky dough)</li>
              <li>Thin, crispy crust</li>
              <li>Shorter shelf life</li>
              <li>Ideal for artisan breads like ciabatta</li>
            </ul>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Very High Hydration (85%+)</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-2">
              <li>Very open, irregular crumb with large holes</li>
              <li>Extremely difficult to handle</li>
              <li>Requires special techniques (coil folds, lamination)</li>
              <li>Often needs support during proofing (banneton)</li>
              <li>Spectacular oven spring when properly executed</li>
              <li>For advanced bakers</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Finding Your Ideal Hydration</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Consider Your Flour</h4>
            <p className="text-sm text-white/80">
              Different brands of the same flour type can vary significantly in protein content and absorption properties. Start with the recommended range for your flour type, then adjust based on results.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Consider Your Environment</h4>
            <p className="text-sm text-white/80">
              Humidity affects flour's water absorption. In dry climates, you may need to increase hydration; in humid environments, you might need to decrease it.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Consider Your Skill Level</h4>
            <p className="text-sm text-white/80">
              If you're new to bread baking, start with lower hydration (65-70%) and gradually increase as you become more comfortable handling wetter doughs.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Incremental Approach</h4>
            <p className="text-sm text-white/80">
              Find your ideal hydration by increasing in small increments (2-5%) with each bake until you reach the sweet spot for your preferences, flour, and environment.
            </p>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Techniques for Higher Hydration Doughs</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Autolyse</h4>
            <p className="text-sm text-white/80">
              Mix flour and water, then rest for 30-60 minutes before adding starter and salt. This allows flour to fully hydrate and begins gluten development, making high-hydration doughs more manageable.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Stretch and Fold / Coil Folds</h4>
            <p className="text-sm text-white/80">
              Instead of traditional kneading, use gentle stretch and fold techniques during bulk fermentation to develop gluten without degassing the dough.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Cold Fermentation</h4>
            <p className="text-sm text-white/80">
              Refrigerating high-hydration dough makes it firmer and easier to handle during shaping.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Wet Hands Technique</h4>
            <p className="text-sm text-white/80">
              Keep your hands wet (not floured) when handling high-hydration dough to prevent sticking.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg text-amber-400 mb-2">Bench Rest</h4>
            <p className="text-sm text-white/80">
              After pre-shaping, allow the dough to rest for 20-30 minutes before final shaping. This relaxes the gluten and makes shaping easier.
            </p>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-amber-400 mb-4">Hydration Calculator for Flour Blends</h3>
        <p className="text-white/80 mb-4">
          When working with multiple flour types, calculate the ideal hydration based on the proportion of each flour:
        </p>
        <ol className="list-decimal list-inside text-sm text-white/80 space-y-2">
          <li>Multiply each flour weight by its ideal hydration percentage</li>
          <li>Add these values together</li>
          <li>Divide by the total flour weight</li>
        </ol>
        <div className="mt-4 p-4 bg-white/10 rounded-lg">
          <p className="text-white/80 font-medium">Example:</p>
          <p className="text-sm text-white/80">
            700g bread flour (70% ideal hydration) + 300g whole wheat flour (80% ideal hydration)
          </p>
          <p className="text-sm text-white/80 mt-2">
            (700g × 70%) + (300g × 80%) = 490g + 240g = 730g water
          </p>
          <p className="text-sm text-white/80 mt-2">
            730g ÷ 1000g total flour = 73% hydration
          </p>
        </div>
      </div>
    </div>
  );
}